import { createElement, useEffect, useState, Fragment } from 'react';
import { useDesignTokens, renderInlineCssVars } from '../../../../theme';
import { Button } from '../../../Button';
import { Select } from '../../../Select';
import { Divider } from '../../../Divider';
import { useNCTextLocalizer } from '../../../../locale/useNCTextLocalizer';
import { Flow } from '../../../Layouts/Flow';
import { withLabels } from '../../../withLabels';
import { OptionPopperItem } from '../../../OptionPopper';
import { useSettingsField } from '../../store/SettingsStore';
import { GlobalSettingsKeys } from '../../GlobalSettingsKeys';
import { AvatarSelectorWithLabels } from '../../../AvatarSelector';
import { useShowMessageToast } from '../../../MessageToast';
import { addBundle } from '../../../../locale/createUseTextLocalizer';
import bundle from '@sas/reacteua-novacmneua/novacmneua-gui-eua.properties';
import { postfixTestId } from '../../../../util/testId';
import { Theme, useSettingsDialogDataProvider } from '../../SettingsDialog';
import { useSettingsService } from '../../GlobalSettingsProvider';
import { useIntl } from '../../hooks/useIntl';

// Import CSS Last
import cn from './GeneralForm.css.json';
import './GeneralForm.css';

addBundle(bundle);

const SelectWithLabel = withLabels(Select);
const ButtonWithLabel = withLabels(Button);

export const GeneralForm = () => {
  const showMessageToast = useShowMessageToast();
  const localize = useNCTextLocalizer('Settings');
  const formName = 'settings-global-generalForm';
  const {
    writeAccessState: { hasWriteAccess },
  } = useSettingsService();

  const [currentTheme, setCurrentTheme, resetCurrentTheme] = useSettingsField(
    GlobalSettingsKeys.THEMES_KEY
  );
  const { 0: suppresedMessages, 2: resetSuppressedMessages } = useSettingsField(
    GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES
  );

  const helpLibraryDetails = {
    contentKey: 'settings.theme',
    library: 'novacmneua-gui-eua',
    testId: postfixTestId(formName, 'themesHelpPopup'),
  };

  const settingsDialogDP = useSettingsDialogDataProvider();

  const hiddenSettings = settingsDialogDP.getHiddenGlobalSettings?.() ?? [];

  const localeInfo = useIntl();

  const [defaultTheme, setDefaultTheme] = useState<string>();
  const [customThemes, setCustomThemes] = useState<Array<Theme>>([]);
  const [systemThemes, setSystemThemes] = useState<Array<Theme>>([]);
  let selectedIndex = 0;

  useEffect(() => {
    let mounted = true;

    const comparator: (a: string, b: string) => number =
      localeInfo?.getCollator().compare ??
      function (a: string, b: string) {
        return a > b ? 1 : -1;
      };

    (Promise as any)
      .allSettled([
        settingsDialogDP.getCustomThemes(),
        settingsDialogDP.getSystemThemes(),
        settingsDialogDP.getDefaultTheme(),
      ])
      .then(([customThemesInfo, systemThemesInfo, defaultThemeInfo]: [any, any, any]) => {
        if (mounted) {
          if ((customThemesInfo && customThemesInfo.status) === 'fulfilled') {
            setCustomThemes(
              (customThemesInfo.value as Theme[]).sort((a, b) => comparator(a.text, b.text))
            );
          }

          if ((systemThemesInfo && systemThemesInfo.status) === 'fulfilled') {
            setSystemThemes(
              (systemThemesInfo.value as Theme[]).sort((a, b) => comparator(a.text, b.text))
            );
          }

          if ((defaultThemeInfo && defaultThemeInfo.status) === 'fulfilled') {
            setDefaultTheme(defaultThemeInfo.value as string);
          }
        }
      });

    return () => {
      mounted = false;
    };
  }, [settingsDialogDP, localeInfo]);

  const defaultThemeItem = { id: '', text: '' };

  if (defaultTheme) {
    defaultThemeItem.text = localize('settings.forms.general.defaultTheme.label', {
      theme: defaultTheme,
    });
  }

  const rawThemeList = [defaultThemeItem, ...systemThemes, ...customThemes];
  if (currentTheme && rawThemeList.length > 0) {
    const index = rawThemeList.findIndex((item) => item.id === currentTheme);
    selectedIndex = index >= 0 ? index : 0;
  }

  const themesList: OptionPopperItem[] = [defaultThemeItem, { separator: true }, ...systemThemes];
  if (customThemes.length > 0) {
    themesList.push({ separator: true }, ...customThemes);
  }

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({ 'spacing-s': dt.spacing.core.s }, 'general-form-avatar');

  return (
    <Flow orientation="vertical" spacing="s">
      <SelectWithLabel
        testId={postfixTestId(formName, 'themes')}
        label={localize('settings.forms.general.themes.label')}
        description={localize('settings.forms.general.themeDescription.label')}
        fieldMinWidth={'100%'}
        fieldSuffixMinWidth={'0'}
        wrapDescription
        labelHelp={helpLibraryDetails}
        values={themesList}
        selectedIndex={selectedIndex}
        onSelected={(index) => {
          index = index ?? 0;
          index > 0 ? setCurrentTheme(rawThemeList[index].id) : resetCurrentTheme();
        }}
      />
      {hasWriteAccess ? (
        <Fragment>
          <Divider />
          <ButtonWithLabel
            testId={postfixTestId(formName, 'resetMessages')}
            label={localize('settings.forms.general.reset.label')}
            text={localize('settings.forms.general.resetMessages.button.label')}
            inputStyle={{ maxWidth: '100%' }}
            fieldSuffixMinWidth={'0'}
            onPress={() => {
              showMessageToast(localize('settings.forms.general.reset.msg'), {
                closeable: false,
              });
              if (suppresedMessages !== undefined) {
                resetSuppressedMessages();
              }
            }}
            labelingAttr="aria-labelledby"
          />
          {!hiddenSettings.includes(GlobalSettingsKeys.Avatar) && (
            <AvatarSelectorWithLabels
              testId={postfixTestId(formName, 'avatar')}
              label={localize('settings.forms.general.avatar.label')}
              fieldSuffixMinWidth={'0'}
              style={tokens}
              className={cn.avatar}
              imageSelectorProps={{ showHintText: true, showSplitButton: true }}
              dataProvider={settingsDialogDP}
            />
          )}
        </Fragment>
      ) : null}
    </Flow>
  );
};
